import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { ChevronRight, Save, Loader2, Share2 } from "lucide-react";

const fmt = (v) =>
  new Intl.NumberFormat("fr-LU", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);

const Line = ({ label, value, strong, deduction, accent }) => (
  <div className={`receipt-line ${strong ? "border-b-2 border-slate-900" : ""}`}>
    <span className={`text-sm ${strong ? "font-serif text-lg text-slate-900" : "font-mono uppercase tracking-[0.1em] text-slate-500 text-xs"}`}>
      {label}
    </span>
    <span className={`tabular font-mono ${strong ? "text-2xl font-medium" : "text-sm"} ${accent ? "text-blue-700" : deduction ? "text-red-700" : "text-slate-900"}`}>
      {deduction && value > 0 ? "− " : ""}{fmt(value)}
    </span>
  </div>
);

const ResultPanel = ({ result, onSave, onShare, scenarioLabel, setScenarioLabel, saving }) => {
  const { t, user } = useApp();
  const [detailed, setDetailed] = useState(false);

  if (!result) {
    return (
      <div className="bg-white border border-slate-200 p-8 sticky top-24" data-testid="result-panel-empty">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-3">{t.result.title}</div>
        <div className="font-serif text-2xl text-slate-300">— · — €</div>
        <p className="text-sm text-slate-500 mt-4 leading-relaxed">
          {t.tagline}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 p-6 md:p-8 sticky top-24" data-testid="result-panel">
      <div className="flex items-baseline justify-between mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">{t.result.title}</div>
        <div className="flex border border-slate-300">
          <button onClick={() => setDetailed(false)} data-testid="view-simple"
            className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest ${!detailed ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}>
            {t.result.simple}
          </button>
          <button onClick={() => setDetailed(true)} data-testid="view-detailed"
            className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest border-l border-slate-300 ${detailed ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}>
            {t.result.detailed}
          </button>
        </div>
      </div>

      <Line label={t.result.gross} value={result.gross_monthly_pension} />
      {detailed && result.forfait_part > 0 && (
        <>
          <Line label={`↳ ${t.result.forfait}`} value={result.forfait_part} />
          <Line label={`↳ ${t.result.proportional}`} value={result.proportional_part} />
        </>
      )}
      <Line label={t.result.health} value={result.health_contribution} deduction />
      <Line label={t.result.dependency} value={result.dependency_contribution} deduction />
      <Line label={t.result.tax} value={result.income_tax} deduction />
      <Line label={t.result.net} value={result.net_monthly_pension} strong accent />

      <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-200">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.result.yearsOfCareer}</div>
          <div className="font-mono tabular text-lg text-slate-900 mt-1" data-testid="result-years">{result.years_of_career}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.result.avgCareer}</div>
          <div className="font-mono tabular text-lg text-slate-900 mt-1" data-testid="result-avg">{fmt(result.avg_career_salary_monthly)}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.result.replacement}</div>
          <div className="font-mono tabular text-lg text-blue-700 mt-1" data-testid="result-replacement">{result.replacement_rate}%</div>
        </div>
      </div>

      {detailed && result.notes?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-200">
          {result.notes.map((n) => (
            <div key={n} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
              <ChevronRight size={12} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-slate-400" />
              <span>{n}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-slate-200">
        {onShare && (
          <button
            onClick={onShare}
            data-testid="btn-share-scenario"
            className="w-full mb-3 px-4 py-3 bg-white border border-slate-300 text-slate-900 text-xs font-mono uppercase tracking-[0.15em] hover:bg-slate-50 flex items-center justify-center gap-2"
          >
            <Share2 size={14} strokeWidth={1.5} />
            {t.nav.share || "Share"}
          </button>
        )}
        {user ? (
          <div className="space-y-3">
            <input
              type="text"
              placeholder={t.form.labelPlaceholder}
              value={scenarioLabel}
              onChange={(e) => setScenarioLabel(e.target.value)}
              data-testid="input-scenario-label"
              className="w-full bg-white border border-slate-300 px-3 py-2.5 text-sm font-mono focus:border-slate-900"
            />
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="btn-save-scenario"
              className="w-full px-4 py-3 bg-blue-700 text-white text-xs font-mono uppercase tracking-[0.15em] hover:bg-blue-800 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.5} />}
              {t.form.save}
            </button>
          </div>
        ) : (
          <div className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 text-center" data-testid="login-prompt">
            {t.result.loginToSave}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultPanel;
